/**
 * SchedulePicker — calendar + time-slot picker for trainer live sessions.
 *
 * Reads a trainer's `weekly_availability` map (shape:
 *   { mon: ["09:00", "14:30"], tue: [...], ... }
 * keys are short day names; values are local-time HH:MM strings).
 * Renders the next 14 days as selectable dates, then renders the slots
 * available for the picked date. Returns an ISO string in UTC when
 * the user confirms.
 *
 * Falls back to a free-text datetime picker if the trainer hasn't
 * published any weekly_availability — that keeps the booking path
 * unblocked for offerings that aren't on a fixed schedule.
 */
import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarIcon as Calendar, ClockIcon as Clock, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LABELS = { sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat" };

function fmtDate(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function SchedulePicker({
  open,
  onOpenChange,
  weeklyAvailability = {},
  durationMin = 45,
  tierLabel = "",
  onConfirm,
}) {
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [manualIso, setManualIso] = useState("");

  const days = useMemo(() => {
    const out = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    for (let i = 1; i <= 14; i += 1) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      out.push(d);
    }
    return out;
  }, []);

  const hasAvailability = Object.values(weeklyAvailability || {}).some((arr) => Array.isArray(arr) && arr.length > 0);
  const slotsForDate = (date) => {
    if (!date) return [];
    const dayKey = DAY_KEYS[date.getDay()];
    return (weeklyAvailability?.[dayKey] || []).slice().sort();
  };

  const confirmFromSlot = () => {
    if (!pickedDate || !pickedSlot) return;
    const [h, m] = pickedSlot.split(":").map((n) => parseInt(n, 10));
    const d = new Date(pickedDate);
    d.setHours(h || 0, m || 0, 0, 0);
    onConfirm(d.toISOString());
  };
  const confirmFromManual = () => {
    if (!manualIso) return;
    const d = new Date(manualIso);
    if (Number.isNaN(d.getTime())) return;
    onConfirm(d.toISOString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="schedule-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl tracking-tight flex items-center gap-2">
            <Calendar size={20} weight="duotone" className="text-[#C1272D]" />
            Pick a time
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#666]">
            {tierLabel ? `Booking: ${tierLabel} · ${durationMin} min. ` : ""}
            {hasAvailability
              ? "Choose a date below, then pick from the trainer's open slots."
              : "This trainer hasn't published recurring availability — pick any date & time and they'll confirm."}
          </DialogDescription>
        </DialogHeader>

        {hasAvailability ? (
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#888] mb-2">Date</p>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="schedule-picker-dates">
                {days.map((d, i) => {
                  const active = pickedDate && d.toDateString() === pickedDate.toDateString();
                  const hasSlots = slotsForDate(d).length > 0;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { setPickedDate(d); setPickedSlot(null); }}
                      disabled={!hasSlots}
                      data-testid={`schedule-date-${i}`}
                      className={
                        "shrink-0 text-center rounded-xl border p-2.5 min-w-[56px] transition-colors " +
                        (active
                          ? "bg-[#0E0E0E] text-white border-[#0E0E0E]"
                          : hasSlots
                            ? "bg-white border-[#E5E5E5] hover:border-[#0E0E0E]"
                            : "bg-[#FAFAFA] border-[#EFEFEF] text-[#BBB] cursor-not-allowed")
                      }
                    >
                      <p className="text-[10px] uppercase tracking-wider">{DAY_LABELS[DAY_KEYS[d.getDay()]]}</p>
                      <p className="font-tabular font-semibold text-base mt-0.5">{d.getDate()}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            {pickedDate && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#888] mb-2 flex items-center gap-1.5">
                  <Clock size={11} /> Slots on {fmtDate(pickedDate)}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="schedule-picker-slots">
                  {slotsForDate(pickedDate).map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setPickedSlot(slot)}
                      data-testid={`schedule-slot-${slot}`}
                      className={
                        "rounded-lg border px-2 py-1.5 text-sm transition-colors " +
                        (pickedSlot === slot
                          ? "bg-[#0E0E0E] text-white border-[#0E0E0E]"
                          : "bg-white border-[#E5E5E5] hover:border-[#0E0E0E]")
                      }
                    >
                      {slot}
                    </button>
                  ))}
                  {slotsForDate(pickedDate).length === 0 && (
                    <p className="col-span-full text-xs text-[#888]">No open slots that day.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="manual-iso" className="text-xs uppercase tracking-wider text-[#888]">Date & time (local)</label>
            <input
              id="manual-iso"
              type="datetime-local"
              value={manualIso}
              onChange={(e) => setManualIso(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[#E5E5E5] bg-white text-sm"
              data-testid="schedule-picker-manual"
              min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
            />
            <p className="text-[11px] text-[#888]">Must be at least 1 hour from now.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="schedule-picker-cancel">Cancel</Button>
          {hasAvailability ? (
            <Button
              onClick={confirmFromSlot}
              disabled={!pickedDate || !pickedSlot}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="schedule-picker-confirm"
            >
              <CheckCircle size={14} className="mr-1.5" weight="fill" /> Confirm time
            </Button>
          ) : (
            <Button
              onClick={confirmFromManual}
              disabled={!manualIso}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="schedule-picker-confirm-manual"
            >
              <CheckCircle size={14} className="mr-1.5" weight="fill" /> Confirm time
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
